import { MenuItem as MuiMenuItem } from "@mui/material";
import { Link } from "react-router-dom";

import { Tooltip } from "ui/tooltip/tooltip";
import { mergeSx } from "ui/merge-sx";
import { MenuItemContent } from "ui/actions-menu/menu-item/menu-item-content";
import { useMenuItem } from "ui/actions-menu/menu-item/use-menu-item";
import {
  ROW_TOOLTIP_WIDTH,
  TRAILING_TOOLTIP_WIDTH,
} from "ui/actions-menu/menu-item/menu-item.constants";
import {
  CRITICAL_ITEM_SX,
  ITEM_SX,
  ROW_TOOLTIP_SX,
  ROW_TOOLTIP_WRAPPER_SX,
  TRAILING_TOOLTIP_SX,
} from "ui/actions-menu/menu-item/menu-item.styles";
import { type MenuItemProps } from "ui/actions-menu/menu-item/menu-item.types";

/**
 * Built on MUI's `MenuItem` so the enclosing `MenuList` can see it: since v9 arrow-key navigation
 * runs on a roving tab index that items register for through `MenuListContext`, and an element that
 * merely carries `role="menuitem"` is invisible to it.
 *
 * `component` keeps the pre-MUI DOM — a real `<button>`, an `<a>` for the router variant, and a
 * `<div>` for the disabled one, which is what lets the panel stay a `<div role="menu">` and the
 * tooltip wrappers stay valid markup.
 */
export const MenuItem = ({
  icon,
  label,
  onClick,
  to,
  disabled,
  isSelected = false,
  color,
  tooltip,
  sx,
  ref,
  ...rest
}: MenuItemProps) => {
  const { isCritical, rowTooltip, trailingTooltip, itemRef, handleClick } = useMenuItem({
    onClick,
    color,
    tooltip,
    ref,
  });

  const itemSx = mergeSx(isCritical ? CRITICAL_ITEM_SX : ITEM_SX, sx);

  if (disabled) {
    const disabledItem = (
      <MuiMenuItem {...rest} component="div" disabled disableGutters ref={itemRef} sx={itemSx}>
        <MenuItemContent
          icon={icon}
          label={label}
          isCritical={isCritical}
          trailing={
            trailingTooltip ? (
              <Tooltip
                label={trailingTooltip.message}
                placement="bottom"
                width={TRAILING_TOOLTIP_WIDTH}
                sx={TRAILING_TOOLTIP_SX}>
                {trailingTooltip.icon}
              </Tooltip>
            ) : undefined
          }
        />
      </MuiMenuItem>
    );

    return rowTooltip ? (
      <Tooltip
        label={rowTooltip}
        placement="bottom"
        width={ROW_TOOLTIP_WIDTH}
        sx={ROW_TOOLTIP_SX}
        wrapperSx={ROW_TOOLTIP_WRAPPER_SX}>
        {disabledItem}
      </Tooltip>
    ) : (
      disabledItem
    );
  }

  const content = <MenuItemContent icon={icon} label={label} isCritical={isCritical} />;

  if (to) {
    return (
      <MuiMenuItem
        {...rest}
        component={Link}
        to={to}
        disableGutters
        selected={isSelected}
        onClick={handleClick}
        ref={itemRef}
        sx={itemSx}>
        {content}
      </MuiMenuItem>
    );
  }

  return (
    <MuiMenuItem
      {...rest}
      component="button"
      type="button"
      disableGutters
      selected={isSelected}
      onClick={handleClick}
      ref={itemRef}
      sx={itemSx}>
      {content}
    </MuiMenuItem>
  );
};
